'use client';

import { Link, usePathname } from '@/i18n/routing';
import { useEffect, useState } from 'react';
import { ArrowRight, BookOpen, ChevronDown, FileText, LayoutTemplate, Menu, Search, ShoppingBag, Store, Upload, User, Wrench, X, Phone } from 'lucide-react';
import { companyInfo } from '@/config/company';
import { CartButton } from './cart-button';
import { FavoritesButton } from './favorites-button';
import { SearchDialog } from './search-dialog';
import { FlagIcon } from '@/components/ui/flag-icon';
import { CategoryIcon } from '@/components/navigation/category-icon';
import { useLocale } from 'next-intl';
import { productPath, productsPath } from '@/lib/localized-paths';
import { categoryLanguageSwitchUrl } from '@/lib/category-language-switch';
import { moneyPageLanguageSwitcherRoutes } from '@/lib/seo/money-pages';
import { fetchSiteSettings } from '@/lib/client/site-settings-cache';
import { SpecialistBrandMark } from '@/components/niche/specialist-brand-mark';

interface Category {
  slug: string;
  name: string;
  count: number;
}

interface NavigationProduct {
  id: string;
  name: string;
  slug: string;
  image: string;
}

interface NavigationGroup {
  id: string;
  label: string;
  description: string;
  icon: string;
  count: number;
  categories: Category[];
  products: NavigationProduct[];
}

interface ClientSiteProfile {
  id: string;
  brandName: string;
  parentBrand: string;
  navigation?: Array<{ label: string; href: string; description?: string }>;
  trustCopy?: { tagline: string; promise: string; footerNote: string } | null;
}

// Alleen de vier talen met complete vertalingen. De routing (src/i18n/routing.ts)
// kent nog 10 locales - bestaande /da/, /it/, /es/, /pl/, /pt/, /cs/-URL's
// blijven dus gewoon werken, maar de switcher linkt er niet meer naar en
// wervt er dus ook niet meer intern naar door. Sitemap en hreflang
// (src/app/sitemap.ts, profile.allowedLocales in site-profiles.ts) waren al
// beperkt tot deze vier; dit sluit de laatste interne-link-route naar de
// onvolledige vertalingen.
const languages = [
  { code: 'nl' as const, name: 'Dutch' },
  { code: 'de' as const, name: 'German' },
  { code: 'en' as const, name: 'English' },
  { code: 'fr' as const, name: 'French' },
];

const productRouteSegments = new Set(['producten', 'produkte', 'products', 'produits']);

const staticRouteBySegment: Record<string, Record<string, string>> = {
  winkelwagen: { nl: '/winkelwagen', de: '/warenkorb', en: '/cart', fr: '/panier' },
  warenkorb: { nl: '/winkelwagen', de: '/warenkorb', en: '/cart', fr: '/panier' },
  cart: { nl: '/winkelwagen', de: '/warenkorb', en: '/cart', fr: '/panier' },
  panier: { nl: '/winkelwagen', de: '/warenkorb', en: '/cart', fr: '/panier' },
  favorieten: { nl: '/favorieten', de: '/favoriten', en: '/favorites', fr: '/favoris' },
  favoriten: { nl: '/favorieten', de: '/favoriten', en: '/favorites', fr: '/favoris' },
  favorites: { nl: '/favorieten', de: '/favoriten', en: '/favorites', fr: '/favoris' },
  favoris: { nl: '/favorieten', de: '/favoriten', en: '/favorites', fr: '/favoris' },
  verzending: { nl: '/verzending', de: '/versand', en: '/shipping', fr: '/livraison' },
  versand: { nl: '/verzending', de: '/versand', en: '/shipping', fr: '/livraison' },
  shipping: { nl: '/verzending', de: '/versand', en: '/shipping', fr: '/livraison' },
  livraison: { nl: '/verzending', de: '/versand', en: '/shipping', fr: '/livraison' },
  retourneren: { nl: '/retourneren', de: '/rueckgabe', en: '/returns', fr: '/retours' },
  rueckgabe: { nl: '/retourneren', de: '/rueckgabe', en: '/returns', fr: '/retours' },
  returns: { nl: '/retourneren', de: '/rueckgabe', en: '/returns', fr: '/retours' },
  retours: { nl: '/retourneren', de: '/rueckgabe', en: '/returns', fr: '/retours' },
  bestandseisen: { nl: '/bestandseisen', de: '/dateianforderungen', en: '/file-requirements', fr: '/exigences-fichiers' },
  dateianforderungen: { nl: '/bestandseisen', de: '/dateianforderungen', en: '/file-requirements', fr: '/exigences-fichiers' },
  'file-requirements': { nl: '/bestandseisen', de: '/dateianforderungen', en: '/file-requirements', fr: '/exigences-fichiers' },
  'exigences-fichiers': { nl: '/bestandseisen', de: '/dateianforderungen', en: '/file-requirements', fr: '/exigences-fichiers' },
  contact: { nl: '/contact', de: '/kontakt', en: '/contact', fr: '/contact' },
  kontakt: { nl: '/contact', de: '/kontakt', en: '/contact', fr: '/contact' },
  templates: { nl: '/templates', de: '/templates', en: '/templates', fr: '/templates' },
  montage: { nl: '/montage', de: '/montage', en: '/installation', fr: '/installation' },
  installation: { nl: '/montage', de: '/montage', en: '/installation', fr: '/installation' },
  'ontwerp-service': { nl: '/ontwerp-service', de: '/design-service', en: '/design-service', fr: '/service-design' },
  'design-service': { nl: '/ontwerp-service', de: '/design-service', en: '/design-service', fr: '/service-design' },
  'service-design': { nl: '/ontwerp-service', de: '/design-service', en: '/design-service', fr: '/service-design' },
  garantie: { nl: '/garantie', de: '/garantie', en: '/guarantee', fr: '/garantie' },
  guarantee: { nl: '/garantie', de: '/garantie', en: '/guarantee', fr: '/garantie' },
  ...moneyPageLanguageSwitcherRoutes(),
};

function getMenuCategoryName(category: Category, locale: string) {
  if (locale === 'fr') {
    if (category.slug === 'accessories-posters') return 'Accessoires posters';
    if (category.slug === 'accessoires') return 'Accessoires de montage';
  }

  if (locale === 'de') {
    if (category.slug === 'accessories-posters') return 'Poster-Zubehör';
    if (category.slug === 'accessoires') return 'Montage-Zubehör';
  }

  if (category.slug === 'accessories-posters') return 'Poster accessoires';
  if (category.slug === 'accessoires') return 'Montage accessoires';

  return category.name;
}

interface HeaderProps {
  initialLogo?: { url: string } | null;
  initialCategoryGroups?: NavigationGroup[];
  initialSiteProfile?: ClientSiteProfile | null;
  /** isResellerPlatformEnabled() van de server; de header is client-side en kan die env niet lezen. */
  resellerProgramEnabled?: boolean;
}

export function Header({ initialLogo = null, initialCategoryGroups = [], initialSiteProfile = null, resellerProgramEnabled = false }: HeaderProps) {
  const locale = useLocale();
  const pathname = usePathname();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeProductMenu, setActiveProductMenu] = useState<string | null>(null);
  const [activeMegaGroup, setActiveMegaGroup] = useState<string | null>(null);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [categoryGroups, setCategoryGroups] = useState<NavigationGroup[]>(initialCategoryGroups);
  const [siteLogo, setSiteLogo] = useState<{ url: string } | null>(initialLogo);
  const [siteProfile, setSiteProfile] = useState<ClientSiteProfile | null>(initialSiteProfile);

  useEffect(() => {
    // Server-side meegegeven menu is al actueel; alleen bijhalen als het er
    // (nog) niet is, zodat het menu niet seconden na hydration invliegt.
    if (initialCategoryGroups.length > 0) return;
    fetch(`/api/navigation/categories?locale=${locale}`)
      .then((res) => res.json())
      .then((data) => setCategoryGroups(data.groups || []))
      .catch((err) => console.error('Error loading categories:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    fetchSiteSettings()
      .then((data: any) => {
        if (data.site) setSiteProfile(data.site);
        if (data.site?.id === 'printen' && data.logo?.url) setSiteLogo({ url: data.logo.url });
        if (data.site?.id && data.site.id !== 'printen') setSiteLogo(null);
      })
      .catch((err) => console.error('Error loading site profile:', err));
  }, []);

  const handleLanguageChange = async (newLocale: string) => {
    setLanguageMenuOpen(false);
    setMobileMenuOpen(false);
    const url = new URL(window.location.href);
    const categoryUrl = categoryLanguageSwitchUrl(url, newLocale);
    if (categoryUrl) {
      window.location.href = categoryUrl;
      return;
    }
    const pathWithoutLocale = url.pathname.replace(/^\/(nl|de|en|fr|da|it|es|pl|pt|cs)(?=\/|$)/, '') || '/';
    const segments = pathWithoutLocale.split('/').filter(Boolean);
    const [firstSegment, secondSegment] = segments;

    if (firstSegment && productRouteSegments.has(firstSegment) && secondSegment) {
      try {
        const currentSlug = decodeURIComponent(secondSegment);
        const response = await fetch(`/api/products/localized-slug?slug=${encodeURIComponent(currentSlug)}&locale=${encodeURIComponent(newLocale)}`);
        if (response.ok) {
          const data = await response.json();
          if (data?.slug) {
            window.location.href = `/${newLocale}${productPath(newLocale, data.slug)}${url.search}${url.hash}`;
            return;
          }
        }
      } catch {
        // Fall back to a route-only locale switch.
      }
    }

    const rewrittenPath = firstSegment && productRouteSegments.has(firstSegment)
      ? productsPath(newLocale)
      : firstSegment && staticRouteBySegment[firstSegment]?.[newLocale]
        ? `${staticRouteBySegment[firstSegment][newLocale]}${segments.length > 1 ? `/${segments.slice(1).map(encodeURIComponent).join('/')}` : ''}`
        : pathWithoutLocale;
    window.location.href = `/${newLocale}${rewrittenPath}${url.search}${url.hash}`;
  };

  const navCopy = {
    nl: {
      products: 'Producten',
      files: 'Bestandseisen',
      templates: 'Templates',
      shipping: 'Verzending',
      contact: 'Contact',
      favorites: 'Favorieten',
      account: 'Account',
      all: 'Alle producten',
      sub: 'Foto, stickers, buitenreclame en drukwerk',
      count: 'producten',
      countSingular: 'product',
      popular: 'Populair',
      topLine: 'Printproducten vanaf 1 stuk',
      promise: 'Veilig betalen - thuisbezorgd in 19 EU-landen',
      tagline: 'Foto, stickers en drukwerk',
      groupLabels: {
        'photo-wall': 'Foto & wand',
        stickers: 'Stickers',
        interior: 'Interieur',
        textile: 'Kleding',
        outdoor: 'Buiten',
        exhibition: 'Beurs & event',
        materials: 'Materialen',
        print: 'Drukwerk',
        gifts: 'Cadeaus',
        accessories: 'Accessoires',
      },
    },
    de: {
      products: 'Produkte',
      files: 'Dateianforderungen',
      templates: 'Templates',
      shipping: 'Versand',
      contact: 'Kontakt',
      favorites: 'Favoriten',
      account: 'Konto',
      all: 'Alle Produkte',
      sub: 'Foto, Aufkleber, Banner und Drucksachen',
      count: 'Produkte',
      countSingular: 'Produkt',
      popular: 'Beliebt',
      groupLabels: {
        'photo-wall': 'Foto & Wand',
        stickers: 'Sticker',
        interior: 'Interieur',
        textile: 'Bekleidung',
        outdoor: 'Außen',
        exhibition: 'Messe & Event',
        materials: 'Materialien',
        print: 'Drucksachen',
        gifts: 'Geschenke',
        accessories: 'Zubehör',
      },
      topLine: 'Druckprodukte ab 1 Stück',
      promise: 'Sicher bezahlen - europaweiter Versand (19 Länder)',
      tagline: 'Foto, Sticker und Drucksachen',
    },
    fr: {
      products: 'Produits',
      files: 'Fichiers d’impression',
      templates: 'Templates',
      shipping: 'Livraison',
      contact: 'Contact',
      favorites: 'Favoris',
      account: 'Compte',
      all: 'Tous les produits',
      sub: 'Photos, stickers, signalétique extérieure et imprimés',
      count: 'produits',
      countSingular: 'produit',
      popular: 'Populaire',
      topLine: 'Produits imprimés dès 1 pièce',
      promise: 'Paiement sécurisé - livraison dans 19 pays européens',
      tagline: 'Photos, stickers et imprimés',
      groupLabels: {
        'photo-wall': 'Photo & mur',
        stickers: 'Stickers',
        interior: 'Intérieur',
        textile: 'Vêtements',
        outdoor: 'Extérieur',
        exhibition: 'Salon & événement',
        materials: 'Matériaux',
        print: 'Imprimés',
        gifts: 'Cadeaux',
        accessories: 'Accessoires',
      },
    },
    en: {
      products: 'Products',
      files: 'File requirements',
      templates: 'Templates',
      shipping: 'Shipping',
      contact: 'Contact',
      favorites: 'Favourites',
      account: 'Account',
      all: 'All products',
      sub: 'Photo, stickers, outdoor advertising and print',
      count: 'products',
      countSingular: 'product',
      popular: 'Popular',
      topLine: 'Print products from 1 piece',
      promise: 'Secure payment - home delivery to 19 European countries',
      tagline: 'Photo, stickers and print',
      groupLabels: {
        'photo-wall': 'Photo & wall',
        stickers: 'Stickers',
        interior: 'Interior',
        textile: 'Clothing',
        outdoor: 'Outdoor',
        exhibition: 'Fair & event',
        materials: 'Materials',
        print: 'Print',
        gifts: 'Gifts',
        accessories: 'Accessories',
      },
    },
  };
  const navLabel = navCopy[locale as keyof typeof navCopy] || navCopy.nl;

  const productOverviewPath = productsPath(locale);
  const isSpecialistSite = Boolean(siteProfile && siteProfile.id !== 'printen');
  const isStickerSite = siteProfile?.id === 'stickers';
  const isBoardSite = siteProfile?.id === 'reclameborden';
  const isPosterSite = siteProfile?.id === 'posters';
  const isCanvasSite = siteProfile?.id === 'canvas';
  const isMapWallSite = siteProfile?.id === 'mapwall';
  const specialistNav = siteProfile?.navigation?.length ? siteProfile.navigation : [];
  const headerBorderClass = isStickerSite ? 'border-lime-200' : isBoardSite ? 'border-amber-300' : isPosterSite ? 'border-red-200' : isCanvasSite ? 'border-stone-200' : isMapWallSite ? 'border-slate-200' : 'border-rose-100';
  const topBarClass = isStickerSite ? 'bg-[#13210f] text-lime-50' : isBoardSite ? 'bg-[#101820] text-amber-50' : isPosterSite ? 'bg-[#171717] text-white' : isCanvasSite ? 'bg-[#3a3027] text-stone-50' : isMapWallSite ? 'bg-slate-950 text-slate-50' : 'bg-[#fff3e4] text-slate-700';
  const hoverClass = isStickerSite ? 'hover:bg-lime-50 hover:text-lime-800' : isBoardSite ? 'hover:bg-amber-50 hover:text-amber-900' : isPosterSite ? 'hover:bg-red-50 hover:text-red-700' : isCanvasSite ? 'hover:bg-stone-100 hover:text-[#6b513b]' : isMapWallSite ? 'hover:bg-slate-100 hover:text-slate-950' : 'hover:bg-rose-50 hover:text-rose-700';
  const activeClass = isStickerSite ? 'bg-lime-50 text-lime-800' : isBoardSite ? 'bg-amber-50 text-amber-900' : isPosterSite ? 'bg-red-50 text-red-700' : isCanvasSite ? 'bg-stone-100 text-[#6b513b]' : isMapWallSite ? 'bg-slate-100 text-slate-950' : 'bg-rose-50 text-rose-700';
  const specialistAllLabel = isStickerSite ? 'Alle stickers' : isBoardSite ? 'Alle reclameborden' : isPosterSite ? 'Alle posters' : isCanvasSite ? 'Alle canvasproducten' : isMapWallSite ? 'Alle kaartposters' : 'Alle producten';
  const specialistMobileAllClass = isStickerSite ? 'bg-lime-50 text-lime-900' : isBoardSite ? 'bg-amber-50 text-amber-950' : isPosterSite ? 'bg-red-50 text-red-900' : isCanvasSite ? 'bg-stone-100 text-[#4b3828]' : isMapWallSite ? 'bg-slate-100 text-slate-950' : 'bg-rose-50 text-rose-900';
  const specialistTopLine = siteProfile?.trustCopy?.tagline || navLabel.topLine;
  const specialistPromise = siteProfile?.trustCopy?.promise || navLabel.promise;
  const isActive = (href: string) => pathname === href || pathname.startsWith(`${href}/`);
  const menuGroups = categoryGroups.filter((group) => group.id !== 'all');
  // Commerciele volgorde voor de sneltabs: cadeaus en drukwerk zijn de grootste
  // groepen en verdienen een eigen tab; de rest blijft bereikbaar via Producten.
  const quickGroupOrder = ['photo-wall', 'stickers', 'gifts', 'textile', 'outdoor', 'print'];
  const quickGroups = quickGroupOrder
    .map((id) => menuGroups.find((group) => group.id === id))
    .filter((group): group is (typeof menuGroups)[number] => Boolean(group));
  const megaGroup = menuGroups.find((group) => group.id === activeMegaGroup) || menuGroups[0];
  const totalCategoryCount = categoryGroups.find((group) => group.id === 'all')?.count || menuGroups.reduce((total, group) => total + group.count, 0);
  const getGroupHref = (group: NavigationGroup) => productsPath(locale, { category: group.id });
  const resellerLabel = locale === 'de' ? 'Wiederverkäufer' : locale === 'fr' ? 'Revendeurs' : locale === 'en' ? 'Resellers' : 'Wederverkopers';
  // Met de wederverkopers-pil erbij schuift elke sneltab één breakpoint op, zodat de
  // nav niet voller wordt: op xl neemt de pil de plek van de vierde tab in.
  const quickGroupVisibility = (index: number) => {
    if (!resellerProgramEnabled) return index >= 4 ? 'hidden 2xl:block' : index === 3 ? 'hidden xl:block 2xl:block' : '';
    if (index >= 5) return 'hidden min-[1700px]:block';
    return index >= 3 ? 'hidden 2xl:block' : '';
  };
  const getSpecialistHref = (href: string) => {
    const productSlug = href.match(/^\/producten\/([^/?#]+)/)?.[1];
    return productSlug ? productPath(locale, productSlug) : href;
  };
  const specialistProductLabel = isStickerSite ? 'Stickers' : isBoardSite ? 'Reclameborden' : isPosterSite ? 'Posters' : isCanvasSite ? 'Canvasproducten' : isMapWallSite ? 'Kaartposters' : 'Producten';

  return (
    <>
      <header className={`sticky top-0 z-50 w-full border-b ${headerBorderClass} bg-white/95 shadow-sm backdrop-blur`}>
        <div className={topBarClass}>
          <div className="container mx-auto flex h-9 items-center justify-between gap-3 px-4 text-xs font-bold">
            <span className={isSpecialistSite ? 'hidden min-w-0 truncate sm:inline' : 'min-w-0 truncate'}>{specialistTopLine}</span>
            <span className="ml-auto hidden truncate lg:inline">{specialistPromise}</span>
            <a href={`tel:${companyInfo.contact.phone.replace(/\s/g, '')}`} className="hidden shrink-0 items-center gap-1 hover:underline md:inline-flex">
              <Phone className="h-3 w-3" />
              {locale === 'nl' ? companyInfo.contact.phoneDisplay : companyInfo.contact.phone}
            </a>
            {isSpecialistSite && (
              <a
                href={`https://www.printen.eu/${locale}`}
                className="inline-flex shrink-0 items-center gap-1 rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-[11px] font-black tracking-wide transition hover:bg-white/20"
              >
                <span className="sm:hidden">Printen.eu</span>
                <span className="hidden sm:inline">Bekijk alles op Printen.eu</span>
                <ArrowRight className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>

        <div className="container mx-auto flex h-16 items-center justify-between gap-3 px-4">
          <Link href="/" className="flex min-w-0 shrink-0 items-center" aria-label={`${siteProfile?.brandName || 'Printen.eu'} home`}>
            {isSpecialistSite ? (
              <SpecialistBrandMark siteId={siteProfile?.id || 'stickers'} compact />
            ) : siteLogo?.url ? (
              /* Reserve 178px for four actions, 12px gap and 32px page gutters,
                 also on 320px screens. Do not let the logo widen the viewport. */
              <img src={siteLogo.url} alt="Printen.eu" className="h-11 max-w-[min(160px,calc(100vw-222px))] object-contain sm:max-w-[190px]" />
            ) : (
              <>
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-600 shadow-md">
                  <span className="text-xl font-black text-white">P</span>
                </div>
                <div className="hidden min-[384px]:block">
                  <div className="whitespace-nowrap text-xl font-black leading-none text-slate-950 sm:text-2xl">
                    Printen<span className="text-rose-600">.eu</span>
                  </div>
                </div>
              </>
            )}
          </Link>

          <nav className="hidden min-w-0 flex-1 flex-nowrap items-center justify-center gap-0.5 overflow-visible lg:flex xl:gap-1">
            {isSpecialistSite && specialistNav.length > 0 ? (
              <>
                <div
                  className="relative"
                  onMouseEnter={() => setActiveProductMenu('specialist')}
                  onMouseLeave={() => setActiveProductMenu(null)}
                >
                  <button
                    type="button"
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-black transition ${isActive(productOverviewPath) ? activeClass : `text-slate-700 ${hoverClass}`}`}
                  >
                    <ShoppingBag className="h-4 w-4" />
                    <span className="whitespace-nowrap">{specialistProductLabel}</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  {activeProductMenu === 'specialist' && (
                    <div className="absolute left-1/2 top-full z-50 w-[560px] max-w-[calc(100vw-2rem)] -translate-x-1/2 pt-2">
                      <div className="rounded-2xl border border-slate-100 bg-white p-3 shadow-xl">
                        <Link
                          href={productOverviewPath}
                          className={`mb-2 flex items-center justify-between rounded-xl px-4 py-3 text-sm font-black transition ${activeClass}`}
                        >
                          <span>{specialistAllLabel}</span>
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                        <div className="grid grid-cols-2 gap-2">
                          {specialistNav.map((item) => (
                            <Link
                              key={item.href}
                              href={getSpecialistHref(item.href)}
                              className={`rounded-xl border border-slate-100 bg-white px-4 py-3 transition ${hoverClass}`}
                            >
                              <span className="block text-sm font-black leading-5 text-slate-950">{item.label}</span>
                              {item.description && (
                                <span className="mt-1 line-clamp-2 block text-xs font-semibold leading-4 text-slate-500">
                                  {item.description}
                                </span>
                              )}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <Link
                  href={productOverviewPath}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition ${isActive(productOverviewPath) ? activeClass : `text-slate-700 ${hoverClass}`}`}
                >
                  <span className="whitespace-nowrap">{specialistAllLabel}</span>
                </Link>
                <Link href="/bestandseisen" className={`inline-flex rounded-full px-4 py-2 text-sm font-bold text-slate-700 transition ${hoverClass}`}>
                  Bestandseisen
                </Link>
                <Link href="/contact" className={`inline-flex rounded-full px-4 py-2 text-sm font-bold text-slate-700 transition ${hoverClass}`}>
                  Advies
                </Link>
              </>
            ) : (
            <>
            {quickGroups.map((group, index) => (
              <div
                key={group.id}
                className={`relative ${quickGroupVisibility(index)}`}
                onMouseEnter={() => setActiveProductMenu(group.id)}
                onMouseLeave={() => setActiveProductMenu(null)}
              >
                <Link
                  href={getGroupHref(group)}
                  onFocus={() => setActiveProductMenu(group.id)}
                  className="flex max-w-[9.5rem] items-center gap-1.5 rounded-full px-2.5 py-2 text-sm font-bold text-slate-700 transition hover:bg-rose-50 hover:text-rose-700 xl:max-w-[10.5rem] xl:px-3"
                >
                  <CategoryIcon name={group.icon} className="h-4 w-4" />
                  <span className="truncate whitespace-nowrap">{group.label}</span>
                  <ChevronDown className="h-4 w-4" />
                </Link>

                {activeProductMenu === group.id && (
                  <div className="absolute left-0 top-full z-50 w-[390px] pt-2">
                    <div className="subtle-scrollbar max-h-[min(70vh,560px)] overflow-y-auto rounded-2xl border bg-white p-3 shadow-xl">
                      <div className="flex items-start gap-3 px-3 pb-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-700">
                          <CategoryIcon name={group.icon} className="h-5 w-5" />
                        </span>
                        <div>
                          <div className="text-sm font-black text-slate-950">{group.label}</div>
                          <div className="mt-0.5 text-xs font-semibold leading-4 text-slate-500">{group.description}</div>
                        </div>
                      </div>
                      <div className={group.categories.length > 8 ? 'grid grid-cols-2 gap-1' : 'space-y-1'}>
                        {group.categories.map((category) => (
                          <Link
                            key={category.slug}
                            href={productsPath(locale, { category: category.slug })}
                            className="block rounded-xl px-3 py-2.5 transition hover:bg-rose-50"
                          >
                            <div className="text-sm font-bold leading-tight text-slate-900">{getMenuCategoryName(category, locale)}</div>
                            <div className="text-xs text-slate-500">{category.count} {category.count === 1 ? navLabel.countSingular : navLabel.count}</div>
                          </Link>
                        ))}
                      </div>
                      {group.products.length > 0 && (
                        <div className="mt-3 grid grid-cols-2 gap-2 border-t pt-3">
                          {group.products.slice(0, 4).map((product) => (
                            <Link
                              key={product.id}
                              href={productPath(locale, product.slug)}
                              className="flex items-center gap-2 rounded-xl px-2 py-2 transition hover:bg-rose-50"
                            >
                              <img src={product.image} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" />
                              <span className="line-clamp-2 text-xs font-bold leading-4 text-slate-700">{product.name}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                      <div className="mt-2 border-t pt-2">
                        <Link href={productOverviewPath} className="block rounded-xl px-3 py-2 text-sm font-black text-rose-700 transition hover:bg-rose-50">
                          {navLabel.all}
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div
              className="relative"
              onMouseEnter={() => {
                setActiveProductMenu('all');
                setActiveMegaGroup((current) => current || menuGroups[0]?.id || null);
              }}
              onMouseLeave={() => setActiveProductMenu(null)}
            >
              <button className={`flex items-center gap-1 rounded-full px-3 py-2 text-sm font-bold transition xl:px-4 ${isActive(productOverviewPath) ? 'bg-rose-50 text-rose-700' : 'text-slate-700 hover:bg-rose-50 hover:text-rose-700'}`}>
                {navLabel.products}
                <ChevronDown className="h-4 w-4" />
              </button>

              {activeProductMenu === 'all' && megaGroup && (
                <div className="absolute right-0 top-full z-50 w-[920px] max-w-[calc(100vw-2rem)] pt-2">
                  <div className="max-h-[calc(100vh-7rem)] overflow-hidden rounded-2xl border bg-white shadow-xl">
                    <div className="grid max-h-[calc(100vh-7rem)] grid-cols-[240px_1fr]">
                      <div className="subtle-scrollbar overflow-y-auto border-r bg-slate-50 p-3">
                        <Link href={productOverviewPath} className="mb-2 block rounded-xl bg-rose-50 px-4 py-3 transition hover:bg-rose-100">
                          <div className="font-black text-rose-700">{navLabel.all}</div>
                          <div className="mt-1 text-xs font-semibold text-slate-600">
                            {totalCategoryCount} {navLabel.count}
                          </div>
                        </Link>
                        <div className="space-y-1">
                          {menuGroups.map((group) => (
                            <Link
                              key={group.id}
                              href={getGroupHref(group)}
                              onMouseEnter={() => setActiveMegaGroup(group.id)}
                              onFocus={() => setActiveMegaGroup(group.id)}
                              className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm font-black transition ${
                                megaGroup.id === group.id ? 'bg-white text-rose-700 shadow-sm' : 'text-slate-700 hover:bg-white hover:text-rose-700'
                              }`}
                            >
                              <span className="flex min-w-0 items-center gap-2">
                                <CategoryIcon name={group.icon} className="h-4 w-4 shrink-0" />
                                <span className="truncate">{group.label}</span>
                              </span>
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{group.count}</span>
                            </Link>
                          ))}
                        </div>
                      </div>

                      <div className="subtle-scrollbar min-h-0 overflow-y-auto p-4 pr-2">
                        <div className="mb-4 flex items-start justify-between gap-4 border-b pb-3">
                          <div>
                            <div className="text-xs font-black uppercase tracking-wide text-slate-400">{navLabel.products}</div>
                            <div className="flex items-center gap-2 text-xl font-black text-slate-950">
                              <CategoryIcon name={megaGroup.icon} className="h-5 w-5 text-rose-600" />
                              {megaGroup.label}
                            </div>
                            <div className="mt-1 text-sm font-semibold text-slate-500">{megaGroup.description}</div>
                          </div>
                          <Link href={productOverviewPath} className="rounded-full bg-rose-600 px-4 py-2 text-sm font-black text-white transition hover:bg-rose-700">
                            {navLabel.all}
                          </Link>
                        </div>

                        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_220px]">
                          <div className="grid gap-1.5 sm:grid-cols-2">
                            {megaGroup.categories.map((category) => (
                              <Link
                                key={category.slug}
                                href={productsPath(locale, { category: category.slug })}
                                className="rounded-xl border border-slate-100 px-3 py-2.5 transition hover:border-rose-100 hover:bg-rose-50"
                              >
                                <div className="text-sm font-bold leading-tight text-slate-950">{getMenuCategoryName(category, locale)}</div>
                                <div className="mt-1 text-xs font-semibold text-slate-500">{category.count} {category.count === 1 ? navLabel.countSingular : navLabel.count}</div>
                              </Link>
                            ))}
                          </div>
                          {megaGroup.products.length > 0 && (
                            <div className="self-start rounded-2xl bg-slate-50 p-3">
                              <div className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">{navLabel.popular}</div>
                              <div className="space-y-2">
                                {megaGroup.products.map((product) => (
                                  <Link
                                    key={product.id}
                                    href={productPath(locale, product.slug)}
                                    className="flex items-center gap-3 rounded-xl bg-white p-2 transition hover:bg-rose-50"
                                  >
                                    <img src={product.image} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />
                                    <span className="line-clamp-2 text-sm font-bold leading-5 text-slate-800">{product.name}</span>
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Services dropdown — uses padding bridge to prevent hover gap */}
            <div className="relative group/svc">
              <button className="peer flex items-center gap-1 rounded-full px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100 xl:px-4">
                Services
                <ChevronDown className="h-3 w-3" />
              </button>
              <div className="absolute left-0 z-50 hidden group-hover/svc:block" style={{ paddingTop: '0.5rem' }}>
                <div className="w-64 rounded-lg border border-gray-200 bg-white shadow-xl py-1">
                  <Link href="/zakelijk" className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-800">
                    <Store className="h-4 w-4 flex-none" />{locale === 'de' ? 'Geschäftskunden' : locale === 'fr' ? 'Espace pro' : locale === 'en' ? 'Business' : 'Zakelijk'}
                  </Link>
                  {resellerProgramEnabled && (
                    <Link href="/wederverkopers" className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-800">
                      <ShoppingBag className="h-4 w-4 flex-none" />{resellerLabel}
                    </Link>
                  )}
                  <Link href="/montage" className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-amber-50 hover:text-amber-800">
                    <Wrench className="h-4 w-4 flex-none" />{locale === 'de' ? 'Montage & Installation' : locale === 'fr' ? 'Montage & installation' : locale === 'en' ? 'Installation service' : 'Montage & Installatie'}
                  </Link>
                  <Link href="/ontwerp-service" className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-green-50 hover:text-green-700">
                    <Upload className="h-4 w-4 flex-none" />{locale === 'de' ? 'Kostenloser Designservice' : locale === 'fr' ? 'Service design gratuit' : locale === 'en' ? 'Free design service' : 'Gratis ontwerpservice'}
                  </Link>
                  <Link href="/templates" className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-blue-700">
                    <LayoutTemplate className="h-4 w-4 flex-none" />{navLabel.templates}
                  </Link>
                  <Link href="/bestandseisen" className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-rose-50 hover:text-rose-700">
                    <FileText className="h-4 w-4 flex-none" />{navLabel.files}
                  </Link>
                  <Link href="/tools/dpi-checker" className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-rose-50 hover:text-rose-700">
                    <FileText className="h-4 w-4 flex-none" />{locale === 'fr' ? 'Vérificateur DPI' : 'DPI-checker'}
                  </Link>
                  <Link href="/kennisbank" className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-indigo-50 hover:text-indigo-700">
                    <BookOpen className="h-4 w-4 flex-none" />{locale === 'de' ? 'Wissensdatenbank' : locale === 'fr' ? 'Base de connaissances' : locale === 'en' ? 'Knowledge base' : 'Kennisbank'}
                  </Link>
                  <Link href="/materialen" className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-indigo-50 hover:text-indigo-700">
                    <BookOpen className="h-4 w-4 flex-none" />{locale === 'de' ? 'Materialien ansehen' : locale === 'fr' ? 'Découvrir les matériaux' : locale === 'en' ? 'Explore materials' : 'Materialen bekijken'}
                  </Link>
                  <Link href="/afwerkingen" className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-indigo-50 hover:text-indigo-700">
                    <BookOpen className="h-4 w-4 flex-none" />{locale === 'de' ? 'Veredelungen ansehen' : locale === 'fr' ? 'Découvrir les finitions' : locale === 'en' ? 'Explore finishes' : 'Afwerkingen bekijken'}
                  </Link>
                </div>
              </div>
            </div>
            {/* Wederverkopers als eigen pil vanaf xl; op lg staat het in Services. */}
            {resellerProgramEnabled && (
              <Link
                href="/wederverkopers"
                className={`hidden items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-2 text-sm font-bold transition xl:inline-flex xl:px-4 ${isActive('/wederverkopers') ? activeClass : `text-slate-700 ${hoverClass}`}`}
              >
                <Store className="h-4 w-4" />
                {resellerLabel}
              </Link>
            )}
            </>
            )}
          </nav>

          <div className="flex shrink-0 items-center gap-1.5 xl:gap-2">
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                setSearchDialogOpen(true);
              }}
              /* Ook op een telefoon zichtbaar. Sinds 11 augustus 2026 stond
                 hier `hidden sm:flex`, en zoeken staat nergens anders in het
                 mobiele menu: op een telefoon was er dus geen enkele manier om
                 iets te zoeken in een winkel met meer dan duizend producten. */
              className={`flex h-10 w-10 items-center justify-center rounded-full transition ${hoverClass}`}
              aria-label="Search"
            >
              <Search className="h-5 w-5 text-slate-600" />
            </button>

            <div className="relative hidden sm:block">
              <button
                onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
                className={`flex items-center gap-2 rounded-full px-3 py-2 text-sm font-bold text-slate-700 transition ${hoverClass}`}
                aria-label="Language"
              >
                <FlagIcon code={locale as 'nl' | 'de' | 'en' | 'fr'} className="h-6 w-6" />
                <ChevronDown className="h-4 w-4" />
              </button>
              {languageMenuOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-44 rounded-xl border bg-white p-1 shadow-xl">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-bold transition ${hoverClass} ${locale === lang.code ? (isStickerSite ? 'text-teal-800' : 'text-rose-700') : 'text-slate-700'}`}
                    >
                      <FlagIcon code={lang.code} className="h-5 w-5" />
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Op een telefoon is het account belangrijker dan het hartje: daar staat het
                accounticoon op de plek van de favorieten (die blijven in het menu), zodat er
                vier iconen blijven: zoeken, account, winkelwagen, menu. Vanaf sm allebei. */}
            <span className="hidden sm:contents">
              <FavoritesButton />
            </span>
            <Link
              href="/account"
              className={`flex h-10 w-10 items-center justify-center rounded-full transition ${hoverClass}`}
              aria-label={navLabel.account}
              title={navLabel.account}
            >
              <User className="h-5 w-5 text-slate-700" />
            </Link>
            <CartButton />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`flex h-10 w-10 items-center justify-center rounded-full transition ${hoverClass} lg:hidden`}
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5 text-slate-700" /> : <Menu className="h-5 w-5 text-slate-700" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="border-t bg-white lg:hidden">
            <div className="subtle-scrollbar container mx-auto max-h-[calc(100vh-6.25rem)] space-y-3 overflow-y-auto px-4 py-5">
              {/* Inloggen bovenaan het mobiele menu: account en het wederverkopersportaal
                  gebruiken dezelfde login. */}
              <div className="grid grid-cols-2 gap-2">
                <Link href="/account" className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-950" onClick={() => setMobileMenuOpen(false)}>
                  <User className="h-4 w-4 text-slate-600" aria-hidden />
                  {navLabel.account}
                </Link>
                <Link href="/account/reseller" className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-950" onClick={() => setMobileMenuOpen(false)}>
                  <Store className="h-4 w-4 text-slate-600" aria-hidden />
                  {locale === 'de' ? 'Händlerportal' : locale === 'fr' ? 'Espace revendeur' : locale === 'en' ? 'Reseller portal' : 'Wederverkopers'}
                </Link>
              </div>
              {isSpecialistSite && specialistNav.length > 0 ? (
                <>
                  <Link href={productOverviewPath} className={`block rounded-xl px-4 py-3 text-base font-black ${specialistMobileAllClass}`} onClick={() => setMobileMenuOpen(false)}>
                    {specialistAllLabel}
                  </Link>
                  {specialistNav.map((item) => (
                    <Link key={item.href} href={getSpecialistHref(item.href)} className="block rounded-xl border border-slate-100 bg-white px-4 py-3" onClick={() => setMobileMenuOpen(false)}>
                      <span className="block text-sm font-black text-slate-950">{item.label}</span>
                      {item.description && <span className="mt-1 block text-xs font-semibold leading-4 text-slate-500">{item.description}</span>}
                    </Link>
                  ))}
                </>
              ) : (
                <>
              <Link href={productOverviewPath} className="block text-base font-black text-slate-950" onClick={() => setMobileMenuOpen(false)}>
                {navLabel.all}
              </Link>
              {menuGroups.map((group) => (
                <details key={group.id} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2" open={menuGroups.length <= 3}>
                  <summary className="cursor-pointer list-none text-sm font-black text-slate-800">
                    <span className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <CategoryIcon name={group.icon} className="h-4 w-4 text-rose-600" />
                        {group.label}
                      </span>
                      <span className="rounded-full bg-white px-2 py-0.5 text-xs text-slate-500">{group.count}</span>
                    </span>
                  </summary>
                  <div className="mt-2 text-xs font-semibold leading-4 text-slate-500">{group.description}</div>
                  <div className="mt-2 grid gap-1">
                    {group.categories.map((category) => (
                      <Link key={category.slug} href={productsPath(locale, { category: category.slug })} className="block rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-700" onClick={() => setMobileMenuOpen(false)}>
                        {getMenuCategoryName(category, locale)}
                      </Link>
                    ))}
                  </div>
                  {group.products.length > 0 && (
                    <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-200 pt-3">
                      {group.products.slice(0, 4).map((product) => (
                        <Link
                          key={product.id}
                          href={productPath(locale, product.slug)}
                          className="flex items-center gap-2 rounded-lg bg-white p-2"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <img src={product.image} alt="" className="h-10 w-10 shrink-0 rounded-md object-cover" />
                          <span className="line-clamp-2 text-xs font-bold leading-4 text-slate-700">{product.name}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </details>
              ))}
                </>
              )}
              <Link href="/bestandseisen" className="block text-base font-black text-slate-950" onClick={() => setMobileMenuOpen(false)}>{navLabel.files}</Link>
              <Link href="/tools/dpi-checker" className="block text-base font-black text-slate-950" onClick={() => setMobileMenuOpen(false)}>{locale === 'fr' ? 'Vérificateur DPI' : 'DPI-checker'}</Link>
              <Link href="/kennisbank" className="block text-base font-black text-slate-950" onClick={() => setMobileMenuOpen(false)}>{locale === 'de' ? 'Wissensdatenbank' : locale === 'fr' ? 'Base de connaissances' : locale === 'en' ? 'Knowledge base' : 'Kennisbank'}</Link>
              <Link href="/materialen" className="block text-base font-black text-slate-950" onClick={() => setMobileMenuOpen(false)}>{locale === 'de' ? 'Materialien ansehen' : locale === 'fr' ? 'Découvrir les matériaux' : locale === 'en' ? 'Explore materials' : 'Materialen bekijken'}</Link>
              <Link href="/afwerkingen" className="block text-base font-black text-slate-950" onClick={() => setMobileMenuOpen(false)}>{locale === 'de' ? 'Veredelungen ansehen' : locale === 'fr' ? 'Découvrir les finitions' : locale === 'en' ? 'Explore finishes' : 'Afwerkingen bekijken'}</Link>
              <Link href="/templates" className="block text-base font-black text-slate-950" onClick={() => setMobileMenuOpen(false)}>{navLabel.templates}</Link>
              <Link href="/favorieten" className="block text-base font-black text-slate-950" onClick={() => setMobileMenuOpen(false)}>{navLabel.favorites}</Link>
              <div className="border-t border-slate-100 pt-3">
                <div className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">Language</div>
                <div className="grid grid-cols-2 gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-bold transition ${
                        locale === lang.code ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-slate-100 bg-white text-slate-700'
                      }`}
                    >
                      <FlagIcon code={lang.code} className="h-5 w-5" />
                      {lang.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      <SearchDialog open={searchDialogOpen} onOpenChange={setSearchDialogOpen} />
    </>
  );
}
